import { trackPromise } from 'react-promise-tracker';
import { camelizeKeys } from 'humps';
import device from './device';
import environment from './environment';
import localStorage from './localStorage';

import RequestError from './requestError';
import { logDebug } from './remoteLogger';

let controller: AbortController;
let routeMapList: ISubdomainRouteMap['list'] = [];

// Session cookie lasts longer than session and can be accessed across tabs
// To persist deviceId and language settings across the tabs, localStorage is used instead of sessionStorage.
let deviceId = localStorage.get<string>('DEVICE_ID') || '';
let clientLanguage = localStorage.get<string>('CLIENT_LANGUAGE') || '';

let currentPartnerId: string;

// Authenticates requests where the session cookie can't be relied on: embedded
// cross-site in a partner iframe, the browser blocks it as a third-party cookie.
// Held in memory only and never persisted, so nothing is left at rest — a
// reload starts without a token, which ends an embedded session.
let sessionJwt = '';

let logoutInterceptorCallback: (() => void) | undefined;

export interface IRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  headers?: Record<string, string>;
  credentials?: 'include' | 'omit' | 'same-origin';
  mode?: 'no-cors' | 'cors' | 'same-origin';
  body?: object;
  formData?: FormData;

  // Meta options.
  assumeJson?: boolean;
  assumeBlob?: boolean;
  silent?: boolean;
  noCamelization?: boolean;
  retryable?: boolean;
  persistent?: boolean;

  // Marks the heartbeat call made to verify session validity, so it isn't
  // itself treated as a trigger for a new verification.
  isSessionCheck?: boolean;
}

export interface ISubdomainRouteMap {
  list: Array<{
    path: string;
    subdomain: string;
  }>;
}

export const setClientLanguageHeader = (language: string) => {
  clientLanguage = language;
  localStorage.set('CLIENT_LANGUAGE', language);
};

export const setDeviceIdHeader = (id: string) => {
  deviceId = id;
  localStorage.set('DEVICE_ID', id);
};

export const setCurrentPartnerId = (id: string) => {
  currentPartnerId = id;
};

// France calls its API cross-origin, so sending the header there needs a CORS
// change on the API first. Every other market calls it same-origin.
const isSessionJwtEnabled = () => environment.COUNTRY !== 'FR';

// When the held token expires, on this device's clock. Worked out from the
// token's own lifetime (`exp - iat`, both from the backend's clock) counted from
// when it arrived, so a device clock that is off doesn't make a fresh token look
// expired. Undefined when the expiry can't be read, leaving the backend to judge.
let sessionJwtExpiresAt: number | undefined;

const decodeJwtExpiresAt = (jwt: string): number | undefined => {
  try {
    const payload = jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const { exp, iat } = JSON.parse(atob(payload)) as {
      exp?: number;
      iat?: number;
    };

    if (typeof exp !== 'number') {
      return undefined;
    }

    return typeof iat === 'number'
      ? Date.now() + (exp - iat) * 1000
      : exp * 1000;
  } catch {
    return undefined;
  }
};

// No token is held in France, so neither the header nor its refresh happens there.
export const setSessionJwt = (jwt: string) => {
  sessionJwt = isSessionJwtEnabled() ? jwt : '';
  sessionJwtExpiresAt = sessionJwt ? decodeJwtExpiresAt(sessionJwt) : undefined;
};

export const clearSessionJwt = () => {
  sessionJwt = '';
  sessionJwtExpiresAt = undefined;
};

export const getSessionJwt = () => sessionJwt;

const isSessionJwtExpired = () =>
  sessionJwtExpiresAt !== undefined && sessionJwtExpiresAt <= Date.now();

export const setSubdomainRouteMap = (map?: ISubdomainRouteMap) => {
  routeMapList = map?.list || [];
};

export const setLogoutInterceptorCallback = (callback: () => void) => {
  logoutInterceptorCallback = callback;
};

export const cancelAllRequests = () => {
  controller.abort();
};

// `includeSessionJwt: false` is for headers that end up in a URL, such as the
// websocket's, where a credential would be written to access logs.
export const getCustomHeaders = ({
  includeSessionJwt = true,
}: { includeSessionJwt?: boolean } = {}) => {
  const sessionJwtHeaderValue =
    includeSessionJwt && !isSessionJwtExpired() ? sessionJwt : '';

  return {
    'X-Client-Version': '20',
    'X-Client-ID': 'kry-web',
    'X-Client-Bundle-ID': 'se.kry.web',
    'X-Client-Device-ID': deviceId,

    'X-Client-TimeZone':
      Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Stockholm',

    'X-Client-Country': environment.COUNTRY,
    'X-Client-Language': clientLanguage,

    'X-System-Version': device.ABBREVIATED_DESCRIPTION,
    'X-System-Language': navigator.language.split(/-|_/)[0],

    ...(!environment.IS_PROD && {
      'X-Client-Is-Testflight': '1',
      'X-Analytics-Debug-Enabled': '1',
    }),

    ...(currentPartnerId && {
      'X-Client-Partner': currentPartnerId,
    }),

    ...(sessionJwtHeaderValue && {
      'kry-user-session-jwt': sessionJwtHeaderValue,
    }),
  };
};

const DEFAULT_CREDENTIALS = 'include';
const NUMBER_OF_RETRIES = 3;

const SESSION_CHECK_MAX_ATTEMPTS = 3;
const SESSION_CHECK_BASE_RETRY_DELAY_MS = 1000;

// Forward-declared: defined below `request`, since it needs to call it.
let verifySessionOrLogout: (
  attempt?: number,
  hadConfirmedInvalidSession?: boolean
) => void;

const request = <T>(url: string, options: IRequestOptions = {}): Promise<T> => {
  controller = new AbortController();

  const requestOptions: RequestInit = {
    method: options.method || 'GET',
    credentials: options.credentials || DEFAULT_CREDENTIALS,
    mode: options.mode,

    // Set custom headers, unless the credential mode is set to "omit"
    // (usually when requesting non-kry/livi services).
    ...(options.credentials !== 'omit' && {
      headers: { ...getCustomHeaders(), ...options.headers },
    }),

    // In certain cases (e.g. for Flex requests), the POST requests need to contain
    // an empty body object. However, the fetch fails if there is a body and the method is GET.
    // Make sure to only include the body in the request if the method is anything but GET.
    body:
      !options.method || options.method?.toUpperCase() === 'GET'
        ? undefined
        : options.formData ||
          (options.body ? JSON.stringify(options.body) : undefined),

    signal: options.persistent ? null : controller.signal,
  };

  // Some api urls need to be routed to a different subdomain.
  const getApiUrlForPath = (path: string) => {
    let resultingUrl = path;
    routeMapList.forEach((match) => {
      if (url.substring(0, match.path.length) === match.path) {
        resultingUrl = environment.getApiUrlForSubdomain(match.subdomain, path);
      }
    });
    return resultingUrl;
  };

  const parseBody = (response: Response): Promise<unknown> => {
    const contentType = response.headers.get('content-type');

    if (options.assumeBlob) {
      return response.blob();
    }

    return (typeof contentType === 'string' &&
      contentType.includes('application/json')) ||
      options.assumeJson
      ? response.json().then((json) => {
          return options.noCamelization
            ? json
            : camelizeKeys(json, (key, convert) =>
                // Don't camelize keys that include uppercase chars, numbers or hyphens.
                // This way, camelization should be reversible.
                /^(?=.*?[A-Z])|(?=.*?[0-9])|(?=.*?[-])/.test(key)
                  ? key
                  : convert(key)
              );
        })
      : response.text();
  };

  const requestPromise = new Promise<T>((resolve, reject) => {
    let attempt = 0;

    const performRequest = () => {
      fetch(getApiUrlForPath(url), requestOptions)
        .then((response) => {
          parseBody(response)
            .then((body) => {
              if (response.ok) {
                resolve(body as T);
              } else {
                const message =
                  (body as any)?.error ||
                  response.statusText ||
                  (typeof body === 'string' ? body : undefined) ||
                  `HTTP ${response.status}`;
                reject(
                  new RequestError(message, response.status, response, body)
                );
              }
            })
            .catch((error) => {
              // Parse error
              Promise.resolve(logDebug('Request parse error', error)).catch(
                () => {}
              );
              reject(new RequestError(error, response.status, response, {}));
            });

          // A 401 means the session may genuinely have expired (as opposed to a
          // 403, which is an authorization issue, or a transient 5xx/gateway
          // blip) — verify via a heartbeat before concluding anything.
          if (
            url !== '/api/view/logout' &&
            !options.isSessionCheck &&
            response.status === 401
          ) {
            verifySessionOrLogout();
          }
        })

        .catch((error) => {
          // If the request was cancelled, reject the promise with a mapped RequestError
          if (error.code === 20) {
            Promise.resolve(
              logDebug('Request error', `${error.message} ${error.code}`, error)
            ).catch(() => {});
            reject(new RequestError(error.message, error.code));
            return;
          }

          // If the request is set to be retryable, idle 500ms and retry.
          if (options.retryable && attempt < NUMBER_OF_RETRIES) {
            attempt++;

            setTimeout(() => {
              performRequest();
            }, 500);

            return;
          }

          reject(error);
        });
    };

    performRequest();
  });

  return options.silent ? requestPromise : trackPromise(requestPromise);
};

// Verifies the session is still valid by sending a heartbeat, retrying a few
// times with backoff before concluding anything. Only a heartbeat that
// itself confirms a 401 is treated as proof the session expired — a 5xx/
// gateway response is still just a transient blip, not evidence of an
// invalid session — and a pure network-layer failure (no response at all,
// e.g. a dropped connection) is inconclusive. A confirmed 401 on an earlier
// attempt is carried forward, so a network blip on the final retry can't
// erase evidence the session already confirmed as expired.
verifySessionOrLogout = (attempt = 0, hadConfirmedInvalidSession = false) => {
  request('/api/user/heartbeat', {
    method: 'PUT',
    silent: true,
    persistent: true,
    isSessionCheck: true,
  }).catch((error) => {
    const isConfirmedInvalidSession =
      hadConfirmedInvalidSession ||
      (error instanceof RequestError && error.status === 401);

    if (attempt < SESSION_CHECK_MAX_ATTEMPTS - 1) {
      setTimeout(
        () => verifySessionOrLogout(attempt + 1, isConfirmedInvalidSession),
        SESSION_CHECK_BASE_RETRY_DELAY_MS * 2 ** attempt
      );
      return;
    }

    if (isConfirmedInvalidSession) {
      logoutInterceptorCallback?.();
    }
  });
};

export default request;
